<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" md="6">
        <h1 class="text-h4 mb-4">Reviews Import</h1>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" lg="10" xl="8">

        <!-- Bulk Import Section -->
        <v-card class="mb-6 pa-4" outlined>
          <v-card-title class="text-h6">
            <v-icon start>mdi-upload</v-icon>
            Bulk Import Reviews
          </v-card-title>
          <v-card-text>
            <p class="text-body-2 mb-4">
              Upload a CSV or Excel file with columns: <code>rating</code> (1-5), <code>text</code>,
              <code>platform</code>, <code>location</code>, <code>brand</code> (optional, defaults to Felice)
            </p>

            <v-file-input
              v-model="importFile"
              label="Select CSV or Excel file"
              accept=".csv, .xlsx, .xls"
              prepend-icon="mdi-file-excel"
              density="comfortable"
              variant="outlined"
              :loading="importLoading"
              :disabled="importLoading"
              show-size
              class="mb-4"
            ></v-file-input>

            <v-row class="mb-4">
              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="defaultBrand"
                  :items="brands"
                  item-title="name"
                  item-value="name"
                  label="Default brand (for rows without brand)"
                  density="comfortable"
                  variant="outlined"
                  hide-details
                ></v-select>
              </v-col>
            </v-row>

            <v-btn
              color="success"
              variant="flat"
              @click="previewImport"
              :disabled="!importFile || importLoading"
              class="mr-2"
            >
              Preview
            </v-btn>

            <v-btn
              color="primary"
              variant="flat"
              @click="confirmImport"
              :disabled="!validCount || importing"
              :loading="importing"
            >
              Import {{ validCount }} Reviews
            </v-btn>

            <span v-if="previewData.length" class="ml-4 text-body-2 text-medium-emphasis">
              {{ previewData.length }} total, {{ validCount }} valid, {{ previewData.length - validCount }} invalid
            </span>
          </v-card-text>

          <!-- Preview Table -->
          <v-expand-transition>
            <div v-if="previewData.length">
              <v-divider class="my-4"></v-divider>

              <div class="d-flex align-center mb-3">
                <h3 class="text-subtitle-1">Preview ({{ previewData.length }} rows)</h3>
                <v-spacer></v-spacer>
                <v-chip-group v-model="previewFilter" class="ml-4">
                  <v-chip filter value="all" size="small">All</v-chip>
                  <v-chip filter value="valid" size="small" color="success">Valid</v-chip>
                  <v-chip filter value="invalid" size="small" color="error">Invalid</v-chip>
                </v-chip-group>
              </div>

              <v-data-table
                :headers="previewHeaders"
                :items="filteredPreviewData"
                :items-per-page="10"
                class="elevation-1"
                fixed-header
                density="comfortable"
                height="calc(100vh - 350px)"
              >
                <template v-slot:item.rating="{ item }">
                  {{ item.rating }} <v-icon color="orange-lighten-3" size="small">mdi-star</v-icon>
                </template>
                <template v-slot:item.brand="{ item }">
                  <v-chip size="x-small" :color="getBrandColor(item.brand)">{{ item.brand }}</v-chip>
                </template>
                <template v-slot:item.valid="{ item }">
                  <v-tooltip :text="item.validationError || 'Valid'" location="left">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" :color="item.valid ? 'success' : 'error'">
                        {{ item.valid ? "mdi-check" : "mdi-alert" }}
                      </v-icon>
                    </template>
                  </v-tooltip>
                </template>
              </v-data-table>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import api from "@/utils/api.js";
import { useConfirmDialog } from "@/composables/useConfirmDialog";
import * as XLSX from "xlsx";
import Papa from "papaparse";

import { useReviews } from "@/composables/useReviews.js";
const { platforms, locations, brands, loadPlatformsAndLocations } = useReviews();

const { confirm } = useConfirmDialog();

const stats = ref({});
const defaultBrand = ref("Felice");
const previewFilter = ref("all");

onMounted(async () => {
  await loadPlatformsAndLocations();
});

const snackbar = ref({
  show: false,
  message: "",
  color: "success",
});

const loadStats = async () => {
  try {
    const res = await api.marketingApi.get("/api/marketing/reviews/admin/stats");
    stats.value = res.data;
  } catch (err) {
    console.error("Failed to load stats:", err);
  }
};

const showSnack = (message, color = "success") => {
  snackbar.value = { show: true, message, color };
};

// Bulk Import State
const importFile = ref(null);
const importLoading = ref(false);
const previewData = ref([]);
const importing = ref(false);

const previewHeaders = [
  { title: "Rating", key: "rating", width: "80" },
  { title: "Text Preview", key: "textPreview" },
  { title: "Platform", key: "platform", width: "100" },
  { title: "Location", key: "location", width: "120" },
  { title: "Brand", key: "brand", width: "100" },
  { title: "Valid?", key: "valid", align: "center", width: "80" },
];

const validCount = computed(() => previewData.value.filter((r) => r.valid).length);

const filteredPreviewData = computed(() => {
  if (previewFilter.value === "valid") {
    return previewData.value.filter((r) => r.valid);
  } else if (previewFilter.value === "invalid") {
    return previewData.value.filter((r) => !r.valid);
  }
  return previewData.value;
});

const getBrandColor = (brand) => {
  const colors = {
    Felice: "primary",
    "Little Italy": "success",
    Fusion: "purple",
  };
  return colors[brand] || "grey";
};

// Get platform/location/brand names for validation
const getPlatformNames = () => platforms.value.map((p) => (typeof p === "string" ? p : p.name));
const getLocationNames = () => locations.value.map((l) => (typeof l === "string" ? l : l.name));
const getBrandNames = () => brands.value.map((b) => (typeof b === "string" ? b : b.name));

// Bulk Import Functions
const previewImport = async () => {
  if (!importFile.value) return;

  importLoading.value = true;
  previewData.value = [];

  try {
    const file = importFile.value;
    const data = await readFile(file);

    let rows = [];
    if (file.name.endsWith(".csv")) {
      const result = Papa.parse(data, {
        header: false,
        skipEmptyLines: true,
      });
      rows = result.data;
    } else {
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    }

    // Detect if first row is header
    const hasHeader =
      rows[0] &&
      rows[0].some(
        (cell) =>
          typeof cell === "string" &&
          ["rating", "text", "platform", "location", "brand"].some((key) =>
            cell.toLowerCase().includes(key),
          ),
      );

    const startIdx = hasHeader ? 1 : 0;
    const parsed = [];
    const platformNames = getPlatformNames();
    const locationNames = getLocationNames();
    const brandNames = getBrandNames();

    for (let i = startIdx; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.every((cell) => cell === null || cell === "")) continue;

      // Handle different column orders
      let rating = null,
        text = "",
        platform = "Talabat",
        location = "Arjan",
        brand = defaultBrand.value;

      if (row.length >= 5) {
        // Order: rating, text, platform, location, brand
        rating = row[0];
        text = row[1] || "";
        platform = row[2] || "Talabat";
        location = row[3] || "Arjan";
        brand = row[4] || defaultBrand.value;
      } else if (row.length >= 4) {
        // Order: rating, text, platform, location
        rating = row[0];
        text = row[1] || "";
        platform = row[2] || "Talabat";
        location = row[3] || "Arjan";
      } else if (row.length === 3) {
        // Assume: rating, text, platform
        rating = row[0];
        text = row[1] || "";
        platform = row[2] || "Talabat";
      } else if (row.length === 2) {
        // Assume: rating, text
        rating = row[0];
        text = row[1] || "";
      } else if (row.length === 1) {
        text = row[0] || "";
      }

      // Validate and convert rating
      const ratingNum = Number(rating);
      const validRating = !isNaN(ratingNum) && ratingNum >= 1 && ratingNum <= 5;

      // Default rating if invalid
      const finalRating = validRating ? ratingNum : 5;

      // Validate platform, location, and brand
      const validPlatform = platformNames.includes(platform);
      const validLocation = locationNames.includes(location);
      const validBrand = brandNames.includes(brand);

      const finalPlatform = validPlatform ? platform : "Talabat";
      const finalLocation = validLocation ? location : "Arjan";
      const finalBrand = validBrand ? brand : defaultBrand.value;

      // Build validation error message
      const errors = [];
      if (text.trim().length <= 10) errors.push("Text too short (min 10 chars)");
      if (!validRating) errors.push(`Invalid rating: ${rating}`);
      if (!validPlatform) errors.push(`Unknown platform: ${platform}`);
      if (!validLocation) errors.push(`Unknown location: ${location}`);
      if (!validBrand) errors.push(`Unknown brand: ${brand}`);

      const valid = errors.length === 0;

      parsed.push({
        rating: finalRating,
        text: text.trim(),
        textPreview: text.trim().substring(0, 60) + (text.trim().length > 60 ? "..." : ""),
        platform: finalPlatform,
        location: finalLocation,
        brand: finalBrand,
        valid,
        validationError: errors.length > 0 ? errors.join("; ") : null,
      });
    }

    previewData.value = parsed;
    showSnack(
      `Preview loaded: ${parsed.length} rows (${parsed.filter((r) => r.valid).length} valid)`,
      "info",
    );
  } catch (err) {
    console.error("Import error:", err);
    showSnack("Failed to read file: " + err.message, "error");
  } finally {
    importLoading.value = false;
  }
};

const readFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);

    if (file.name.endsWith(".csv")) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
};

const confirmImport = async () => {
  const validReviews = previewData.value.filter((r) => r.valid);
  if (validReviews.length === 0) {
    showSnack("No valid reviews to import", "warning");
    return;
  }

  const confirmed = await confirm({
    title: "Import Reviews",
    message: `Import ${validReviews.length} valid reviews?`,
    type: "info",
    confirmText: "Import",
  });
  if (!confirmed) return;

  importing.value = true;

  try {
    // Use bulk import API for better performance
    const response = await api.marketingApi.post("/api/marketing/reviews/admin/reviews/bulk", {
      reviews: validReviews.map((r) => ({
        rating: r.rating,
        text: r.text,
        platform: r.platform,
        location: r.location,
        brand: r.brand,
      })),
    });

    const { imported, failed } = response.data;
    showSnack(
      `Imported ${imported} reviews${failed > 0 ? `, ${failed} failed` : ""}`,
      imported > 0 ? "success" : "warning",
    );
    previewData.value = [];
    importFile.value = null;
    await loadStats();
  } catch (err) {
    console.error("Bulk import failed:", err);
    showSnack(err.response?.data?.error || "Bulk import failed", "error");
  } finally {
    importing.value = false;
  }
};
</script>
